import React from "react";
import { createPortal } from "react-dom";
import type { AgentVisualizationData } from "./AgentVisualization";
import {
  Aggregation,
  BubblePoint,
  ChartBuilderConfig,
  ChartFilter,
  ChartType,
  PivotRow,
  PivotSeries,
  buildBubblePoints,
  buildPivotRows,
  createDefaultChartConfig,
  getPivotSeries,
  isDimensionColumn,
  isNumericColumn,
  validateChartConfig,
} from "./chartBuilderModel";
import "./ChartBuilder.css";

type ChartBuilderProps = {
  data: AgentVisualizationData;
  onClose: () => void;
};

type Appearance = {
  showLegend: boolean;
  showLabels: boolean;
  showGrid: boolean;
  stacked: boolean;
  donut: boolean;
  showPoints: boolean;
  palette: "emerald" | "ocean" | "orchard";
};

type FieldRole =
  | "category"
  | "values"
  | "series"
  | "x"
  | "y"
  | "size"
  | "label"
  | "color"
  | "filter";

type TooltipState = {
  x: number;
  y: number;
  title: string;
  lines: string[];
};

const palettes: Record<Appearance["palette"], string[]> = {
  emerald: ["#42d486", "#70a7ff", "#f2b657", "#a98bf4", "#f07fa5", "#42c7c6", "#f37c6b", "#98c761"],
  ocean: ["#60a5fa", "#22d3ee", "#818cf8", "#34d399", "#fbbf24", "#f472b6", "#38bdf8", "#a78bfa"],
  orchard: ["#7bc47f", "#f0a868", "#d67575", "#9d8ac7", "#6cb6b1", "#d6bd67", "#b47f9b", "#7e9f6e"],
};

const chartTypeLabels: Record<ChartType, string> = {
  bar: "Bar",
  line: "Line",
  pie: "Pie",
  bubble: "Bubble",
};

const chartTypeIcons: Record<ChartType, string> = {
  bar: "▥",
  line: "⌁",
  pie: "◒",
  bubble: "◉",
};

const aggregationLabels: Record<Aggregation, string> = {
  sum: "Sum",
  average: "Average",
  count: "Count",
  min: "Minimum",
  max: "Maximum",
};

const finiteNumbers = (values: Array<number | null | undefined>) =>
  values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));

const extent = (values: number[], includeZero = true): [number, number] => {
  let minimum = values.length ? Math.min(...values) : 0;
  let maximum = values.length ? Math.max(...values) : 1;
  if (includeZero) {
    minimum = Math.min(0, minimum);
    maximum = Math.max(0, maximum);
  }
  if (minimum === maximum) {
    const padding = Math.abs(minimum || 1) * 0.2;
    minimum -= padding;
    maximum += padding;
  }
  return [minimum, maximum];
};

const clamp = (value: number, minimum: number, maximum: number) => Math.min(maximum, Math.max(minimum, value));

const fieldLabel = (data: AgentVisualizationData, key?: string) =>
  data.columns.find((column) => column.key === key)?.label ?? key?.replace(/_/g, " ") ?? "";

const fieldUnit = (data: AgentVisualizationData, key?: string) =>
  (key ? data.units?.[key] : undefined) ?? data.columns.find((column) => column.key === key)?.unit ?? undefined;

const aggregatedUnit = (data: AgentVisualizationData, key: string | undefined, aggregation: Aggregation) =>
  aggregation === "count" ? undefined : fieldUnit(data, key);

const aggregatedSeriesLabel = (series: PivotSeries, aggregation: Aggregation) =>
  aggregation === "count" ? `Count of ${series.label}` : series.label;

const formatNumber = (value: number, unit?: string, compact = false) => {
  const options: Intl.NumberFormatOptions = compact
    ? { notation: "compact", maximumFractionDigits: 1 }
    : { maximumFractionDigits: Math.abs(value) < 10 && value % 1 !== 0 ? 2 : 0 };
  if (unit === "currency") {
    return new Intl.NumberFormat(undefined, { ...options, style: "currency", currency: "USD" }).format(value);
  }
  if (unit === "percentage") return `${new Intl.NumberFormat(undefined, options).format(value)}%`;
  return new Intl.NumberFormat(undefined, options).format(value);
};

const shorten = (value: string, length = 15) => value.length > length ? `${value.slice(0, length - 1)}…` : value;

const safeCsvCell = (value: unknown): string => {
  const text = value == null ? "" : String(value);
  const formulaSafe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(formulaSafe) ? `"${formulaSafe.replace(/"/g, '""')}"` : formulaSafe;
};

const downloadText = (filename: string, value: string, mimeType: string) => {
  const blob = new Blob(["\uFEFF", value], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const chartFilename = (title: string) =>
  `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "lenses-chart"}.csv`;

function SvgTooltipTarget({
  children,
  title,
  lines,
  onTooltip,
}: {
  children: React.ReactElement;
  title: string;
  lines: string[];
  onTooltip: (tooltip: TooltipState | null) => void;
}) {
  return React.cloneElement(children as React.ReactElement<any>, {
    onPointerMove: (event: React.PointerEvent<SVGElement>) => onTooltip({
      x: event.clientX + 12,
      y: event.clientY + 12,
      title,
      lines,
    }),
    onPointerLeave: () => onTooltip(null),
  });
}

function GridLines({
  left,
  top,
  width,
  height,
  minimum,
  maximum,
  unit,
}: {
  left: number;
  top: number;
  width: number;
  height: number;
  minimum: number;
  maximum: number;
  unit?: string;
}) {
  return <g aria-hidden="true">
    {[0, 1, 2, 3, 4].map((index) => {
      const ratio = index / 4;
      const y = top + height - ratio * height;
      const value = minimum + ratio * (maximum - minimum);
      return <g key={index}>
        <line className="chart-builder-grid-line" x1={left} x2={left + width} y1={y} y2={y} />
        <text className="chart-builder-axis-label" x={left - 8} y={y + 3} textAnchor="end">
          {formatNumber(value, unit, true)}
        </text>
      </g>;
    })}
  </g>;
}

function BarChart({
  data,
  rows,
  series,
  aggregation,
  appearance,
  colors,
  onTooltip,
}: {
  data: AgentVisualizationData;
  rows: PivotRow[];
  series: PivotSeries[];
  aggregation: Aggregation;
  appearance: Appearance;
  colors: string[];
  onTooltip: (tooltip: TooltipState | null) => void;
}) {
  const width = 800;
  const height = 340;
  const margin = { top: 18, right: 18, bottom: 62, left: 64 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const stackedExtents = rows.flatMap((row) => {
    const values = series.map((item) => row.values[item.key] ?? 0);
    return [values.filter((value) => value >= 0).reduce((sum, value) => sum + value, 0), values.filter((value) => value < 0).reduce((sum, value) => sum + value, 0)];
  });
  const values = appearance.stacked
    ? stackedExtents
    : finiteNumbers(rows.flatMap((row) => series.map((item) => row.values[item.key])));
  const [minimum, maximum] = extent(values);
  const range = maximum - minimum;
  const yFor = (value: number) => margin.top + (maximum - value) / range * plotHeight;
  const zeroY = yFor(0);
  const slot = plotWidth / Math.max(1, rows.length);
  const groupWidth = Math.min(slot * 0.72, 66);
  const barWidth = appearance.stacked ? groupWidth : Math.max(3, groupWidth / Math.max(1, series.length));
  const primaryUnit = aggregatedUnit(data, series[0]?.valueField, aggregation);

  return <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${data.title} bar chart`}>
    {appearance.showGrid ? <GridLines {...margin} width={plotWidth} height={plotHeight} minimum={minimum} maximum={maximum} unit={primaryUnit} /> : null}
    <line x1={margin.left} x2={margin.left + plotWidth} y1={zeroY} y2={zeroY} stroke="rgba(203,213,225,.34)" />
    {rows.map((row, rowIndex) => {
      let positiveStack = 0;
      let negativeStack = 0;
      return <g key={`${row.category}-${rowIndex}`}>
        {series.map((item, seriesIndex) => {
          const value = row.values[item.key];
          if (value == null) return null;
          let start = 0;
          let end = value;
          if (appearance.stacked) {
            start = value >= 0 ? positiveStack : negativeStack;
            end = start + value;
            if (value >= 0) positiveStack = end;
            else negativeStack = end;
          }
          const x = margin.left + rowIndex * slot + (slot - groupWidth) / 2 + (appearance.stacked ? 0 : seriesIndex * barWidth);
          const y = Math.min(yFor(start), yFor(end));
          const renderedHeight = Math.max(1, Math.abs(yFor(start) - yFor(end)));
          const labelY = value >= 0 ? y - 5 : y + renderedHeight + 11;
          return <React.Fragment key={item.key}>
            <SvgTooltipTarget
              title={row.category}
              lines={[`${aggregatedSeriesLabel(item, aggregation)}: ${formatNumber(value, aggregatedUnit(data, item.valueField, aggregation))}`]}
              onTooltip={onTooltip}
            >
              <rect
                className="chart-builder-bar"
                x={x}
                y={y}
                width={Math.max(2, appearance.stacked ? barWidth : barWidth - 2)}
                height={renderedHeight}
                rx={Math.min(5, barWidth / 3)}
                fill={colors[seriesIndex % colors.length]}
                opacity={0.9}
              />
            </SvgTooltipTarget>
            {appearance.showLabels && renderedHeight > 8 ? <text
              className="chart-builder-value-label"
              x={x + barWidth / 2}
              y={clamp(labelY, margin.top + 9, margin.top + plotHeight + 13)}
              textAnchor="middle"
            >{formatNumber(value, aggregatedUnit(data, item.valueField, aggregation), true)}</text> : null}
          </React.Fragment>;
        })}
        <text
          className="chart-builder-axis-label"
          x={margin.left + rowIndex * slot + slot / 2}
          y={margin.top + plotHeight + 18}
          textAnchor="end"
          transform={rows.length > 5 ? `rotate(-38 ${margin.left + rowIndex * slot + slot / 2} ${margin.top + plotHeight + 18})` : undefined}
        >{shorten(row.category, rows.length > 8 ? 10 : 15)}</text>
      </g>;
    })}
  </svg>;
}

function LineChart({
  data,
  rows,
  series,
  aggregation,
  appearance,
  colors,
  onTooltip,
}: {
  data: AgentVisualizationData;
  rows: PivotRow[];
  series: PivotSeries[];
  aggregation: Aggregation;
  appearance: Appearance;
  colors: string[];
  onTooltip: (tooltip: TooltipState | null) => void;
}) {
  const width = 800;
  const height = 340;
  const margin = { top: 20, right: 22, bottom: 58, left: 64 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const values = finiteNumbers(rows.flatMap((row) => series.map((item) => row.values[item.key])));
  const [minimum, maximum] = extent(values);
  const range = maximum - minimum;
  const xFor = (index: number) => margin.left + (rows.length <= 1 ? plotWidth / 2 : index / (rows.length - 1) * plotWidth);
  const yFor = (value: number) => margin.top + (maximum - value) / range * plotHeight;
  const primaryUnit = aggregatedUnit(data, series[0]?.valueField, aggregation);

  return <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${data.title} line chart`}>
    {appearance.showGrid ? <GridLines {...margin} width={plotWidth} height={plotHeight} minimum={minimum} maximum={maximum} unit={primaryUnit} /> : null}
    {series.map((item, seriesIndex) => {
      type LinePoint = { x: number; y: number; value: number; row: PivotRow };
      const segments: LinePoint[][] = [];
      let currentSegment: LinePoint[] = [];
      rows.forEach((row, index) => {
        const value = row.values[item.key];
        if (value == null) {
          if (currentSegment.length) segments.push(currentSegment);
          currentSegment = [];
          return;
        }
        currentSegment.push({ x: xFor(index), y: yFor(value), value, row });
      });
      if (currentSegment.length) segments.push(currentSegment);
      const points = segments.flat();
      return <g key={item.key}>
        {segments.filter((segment) => segment.length > 1).map((segment, segmentIndex) => <polyline
          key={segmentIndex}
          points={segment.map((point) => `${point.x},${point.y}`).join(" ")}
          fill="none"
          stroke={colors[seriesIndex % colors.length]}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />)}
        {points.map((point, pointIndex) => <React.Fragment key={`${point.row.category}-${pointIndex}`}>
          <SvgTooltipTarget
            title={point.row.category}
            lines={[`${aggregatedSeriesLabel(item, aggregation)}: ${formatNumber(point.value, aggregatedUnit(data, item.valueField, aggregation))}`]}
            onTooltip={onTooltip}
          >
            <circle
              className="chart-builder-line-point"
              cx={point.x}
              cy={point.y}
              r={appearance.showPoints || points.length === 1 ? 4 : 2.4}
              fill={colors[seriesIndex % colors.length]}
              stroke="#0a1018"
              strokeWidth="2"
            />
          </SvgTooltipTarget>
          {appearance.showLabels ? <text className="chart-builder-value-label" x={point.x} y={point.y - 8} textAnchor="middle">
            {formatNumber(point.value, aggregatedUnit(data, item.valueField, aggregation), true)}
          </text> : null}
        </React.Fragment>)}
      </g>;
    })}
    {rows.map((row, index) => <text
      key={`${row.category}-${index}`}
      className="chart-builder-axis-label"
      x={xFor(index)}
      y={margin.top + plotHeight + 18}
      textAnchor={rows.length > 6 ? "end" : "middle"}
      transform={rows.length > 6 ? `rotate(-38 ${xFor(index)} ${margin.top + plotHeight + 18})` : undefined}
    >{shorten(row.category, rows.length > 8 ? 10 : 15)}</text>)}
  </svg>;
}

const polar = (centerX: number, centerY: number, radius: number, angle: number) => {
  const radians = (angle - 90) * Math.PI / 180;
  return { x: centerX + radius * Math.cos(radians), y: centerY + radius * Math.sin(radians) };
};

const arcPath = (centerX: number, centerY: number, outerRadius: number, startAngle: number, endAngle: number, innerRadius = 0) => {
  const startOuter = polar(centerX, centerY, outerRadius, endAngle);
  const endOuter = polar(centerX, centerY, outerRadius, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  if (innerRadius <= 0) {
    return [`M ${centerX} ${centerY}`, `L ${startOuter.x} ${startOuter.y}`, `A ${outerRadius} ${outerRadius} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`, "Z"].join(" ");
  }
  const startInner = polar(centerX, centerY, innerRadius, endAngle);
  const endInner = polar(centerX, centerY, innerRadius, startAngle);
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${endInner.x} ${endInner.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 1 ${startInner.x} ${startInner.y}`,
    "Z",
  ].join(" ");
};

function PieChart({
  data,
  rows,
  series,
  aggregation,
  appearance,
  colors,
  onTooltip,
}: {
  data: AgentVisualizationData;
  rows: PivotRow[];
  series: PivotSeries[];
  aggregation: Aggregation;
  appearance: Appearance;
  colors: string[];
  onTooltip: (tooltip: TooltipState | null) => void;
}) {
  const item = series[0];
  const slices = item ? rows.map((row) => ({ row, value: row.values[item.key] ?? 0 })).filter((entry) => entry.value > 0) : [];
  const total = slices.reduce((sum, entry) => sum + entry.value, 0);
  let runningAngle = 0;
  const centerX = 400;
  const centerY = 170;
  const radius = 132;
  return <svg viewBox="0 0 800 340" role="img" aria-label={`${data.title} pie chart`}>
    {slices.map((slice, index) => {
      const startAngle = runningAngle;
      const angle = total ? slice.value / total * 360 : 0;
      const endAngle = startAngle + angle;
      runningAngle = endAngle;
      const midPoint = polar(centerX, centerY, appearance.donut ? radius * 0.73 : radius * 0.62, startAngle + angle / 2);
      const percent = total ? slice.value / total * 100 : 0;
      return <React.Fragment key={`${slice.row.category}-${index}`}>
        <SvgTooltipTarget
          title={slice.row.category}
          lines={[
            `${formatNumber(slice.value, aggregatedUnit(data, item?.valueField, aggregation))}`,
            `${percent.toFixed(percent < 10 ? 1 : 0)}% of total`,
          ]}
          onTooltip={onTooltip}
        >
          <path
            className="chart-builder-bar"
            d={arcPath(centerX, centerY, radius, startAngle, endAngle, appearance.donut ? 70 : 0)}
            fill={colors[index % colors.length]}
            stroke="#0a1018"
            strokeWidth="2"
          />
        </SvgTooltipTarget>
        {appearance.showLabels && percent >= 4 ? <text className="chart-builder-value-label" x={midPoint.x} y={midPoint.y + 3} textAnchor="middle">
          {`${percent.toFixed(0)}%`}
        </text> : null}
      </React.Fragment>;
    })}
    {appearance.donut ? <g aria-hidden="true">
      <text x={centerX} y={centerY - 3} textAnchor="middle" fill="#748195" fontSize="10" fontWeight="750">TOTAL</text>
      <text x={centerX} y={centerY + 19} textAnchor="middle" fill="#f2f6fb" fontSize="18" fontWeight="800">
        {formatNumber(total, aggregatedUnit(data, item?.valueField, aggregation), true)}
      </text>
    </g> : null}
  </svg>;
}

function BubbleChart({
  data,
  points,
  config,
  appearance,
  colors,
  onTooltip,
}: {
  data: AgentVisualizationData;
  points: BubblePoint[];
  config: ChartBuilderConfig;
  appearance: Appearance;
  colors: string[];
  onTooltip: (tooltip: TooltipState | null) => void;
}) {
  const width = 800;
  const height = 340;
  const margin = { top: 18, right: 26, bottom: 54, left: 68 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const [minimumX, maximumX] = extent(points.map((point) => point.x), false);
  const [minimumY, maximumY] = extent(points.map((point) => point.y), false);
  const sizes = points.map((point) => point.size);
  const minimumSize = Math.min(...sizes, 0);
  const maximumSize = Math.max(...sizes, 1);
  const sizeRange = Math.max(1, maximumSize - minimumSize);
  const xFor = (value: number) => margin.left + (value - minimumX) / (maximumX - minimumX) * plotWidth;
  const yFor = (value: number) => margin.top + (maximumY - value) / (maximumY - minimumY) * plotHeight;
  const radiusFor = (value: number) => 7 + Math.sqrt((value - minimumSize) / sizeRange) * 24;
  const colorValues = Array.from(new Set(points.map((point) => point.color ?? "All points")));
  const xUnit = fieldUnit(data, config.xField);
  const yUnit = fieldUnit(data, config.yField);
  const sizeUnit = fieldUnit(data, config.sizeField);
  return <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${data.title} bubble chart`}>
    {appearance.showGrid ? <g aria-hidden="true">
      {[0, 1, 2, 3, 4].map((index) => {
        const ratio = index / 4;
        const x = margin.left + ratio * plotWidth;
        const y = margin.top + plotHeight - ratio * plotHeight;
        return <React.Fragment key={index}>
          <line className="chart-builder-grid-line" x1={x} x2={x} y1={margin.top} y2={margin.top + plotHeight} />
          <line className="chart-builder-grid-line" x1={margin.left} x2={margin.left + plotWidth} y1={y} y2={y} />
          <text className="chart-builder-axis-label" x={x} y={margin.top + plotHeight + 18} textAnchor="middle">
            {formatNumber(minimumX + ratio * (maximumX - minimumX), xUnit, true)}
          </text>
          <text className="chart-builder-axis-label" x={margin.left - 8} y={y + 3} textAnchor="end">
            {formatNumber(minimumY + ratio * (maximumY - minimumY), yUnit, true)}
          </text>
        </React.Fragment>;
      })}
    </g> : null}
    <text className="chart-builder-axis-label" x={margin.left + plotWidth / 2} y={height - 6} textAnchor="middle">
      {fieldLabel(data, config.xField)}
    </text>
    <text className="chart-builder-axis-label" x="12" y={margin.top + plotHeight / 2} textAnchor="middle" transform={`rotate(-90 12 ${margin.top + plotHeight / 2})`}>
      {fieldLabel(data, config.yField)}
    </text>
    {points.map((point, index) => {
      const colorIndex = colorValues.indexOf(point.color ?? "All points");
      const radius = radiusFor(point.size);
      const x = xFor(point.x);
      const y = yFor(point.y);
      return <React.Fragment key={`${point.rowIndex}-${index}`}>
        <SvgTooltipTarget
          title={point.label}
          lines={[
            `${fieldLabel(data, config.xField)}: ${formatNumber(point.x, xUnit)}`,
            `${fieldLabel(data, config.yField)}: ${formatNumber(point.y, yUnit)}`,
            `${fieldLabel(data, config.sizeField)}: ${formatNumber(point.size, sizeUnit)}`,
            ...(point.color ? [`${fieldLabel(data, config.colorField)}: ${point.color}`] : []),
          ]}
          onTooltip={onTooltip}
        >
          <circle
            className="chart-builder-bubble"
            cx={x}
            cy={y}
            r={radius}
            fill={colors[colorIndex % colors.length]}
            fillOpacity="0.72"
            stroke={colors[colorIndex % colors.length]}
            strokeWidth="1.5"
          />
        </SvgTooltipTarget>
        {appearance.showLabels && radius >= 12 ? <text className="chart-builder-value-label" x={x} y={y + 3} textAnchor="middle">
          {shorten(point.label, 11)}
        </text> : null}
      </React.Fragment>;
    })}
  </svg>;
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return <label className="chart-builder-toggle-row">
    <span>{label}</span>
    <span className="chart-builder-toggle">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span aria-hidden="true" />
    </span>
  </label>;
}

function ChartWell({
  label,
  role,
  placeholder,
  fields,
  data,
  dragOverRole,
  onDropField,
  onRemoveField,
}: {
  label: string;
  role: FieldRole;
  placeholder: string;
  fields: string[];
  data: AgentVisualizationData;
  dragOverRole: FieldRole | null;
  onDropField: (role: FieldRole, key: string) => void;
  onRemoveField: (role: FieldRole, key: string) => void;
}) {
  const [isOver, setIsOver] = React.useState(false);
  return <div
    className={`chart-builder-well${isOver || dragOverRole === role ? " is-over" : ""}`}
    role="group"
    aria-label={`${label} field well`}
    tabIndex={0}
    onDragEnter={(event) => { event.preventDefault(); setIsOver(true); }}
    onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; }}
    onDragLeave={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsOver(false);
    }}
    onDrop={(event) => {
      event.preventDefault();
      setIsOver(false);
      const key = event.dataTransfer.getData("application/x-lenses-field") || event.dataTransfer.getData("text/plain");
      if (key) onDropField(role, key);
    }}
  >
    <span className="chart-builder-well-label">{label}</span>
    {fields.length ? <div className="chart-builder-pill-row">{fields.map((key) => {
      const column = data.columns.find((item) => item.key === key);
      return <span className={`chart-builder-pill${column && isNumericColumn(column) ? " is-measure" : ""}`} key={key}>
        <span>{fieldLabel(data, key)}</span>
        <button type="button" aria-label={`Remove ${fieldLabel(data, key)} from ${label}`} onClick={() => onRemoveField(role, key)}>×</button>
      </span>;
    })}</div> : <span className="chart-builder-well-placeholder">{placeholder}</span>}
  </div>;
}

const sourceSummary = (data: AgentVisualizationData) => (data.sources ?? []).map((source) => [
  source.connector,
  source.entity,
  source.record_count != null ? `${source.record_count.toLocaleString()} source records` : "",
].filter(Boolean).join(" · ")).join("; ");

export default function ChartBuilder({ data, onClose }: ChartBuilderProps) {
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const backdropRef = React.useRef<HTMLDivElement | null>(null);
  const defaultConfig = React.useMemo(() => ({
    ...createDefaultChartConfig(data),
    topN: Math.min(12, Math.max(1, data.rows.length)),
  }), [data]);
  const [config, setConfig] = React.useState<ChartBuilderConfig>(defaultConfig);
  const [appearance, setAppearance] = React.useState<Appearance>({
    showLegend: true,
    showLabels: false,
    showGrid: true,
    stacked: false,
    donut: true,
    showPoints: true,
    palette: "emerald",
  });
  const [fieldSearch, setFieldSearch] = React.useState("");
  const [dragOverRole, setDragOverRole] = React.useState<FieldRole | null>(null);
  const [showData, setShowData] = React.useState(true);
  const [tooltip, setTooltip] = React.useState<TooltipState | null>(null);
  const numericColumns = React.useMemo(() => data.columns.filter(isNumericColumn), [data]);
  const dimensionColumns = React.useMemo(() => data.columns.filter(isDimensionColumn), [data]);
  const filteredDimensions = dimensionColumns.filter((column) => column.label.toLowerCase().includes(fieldSearch.toLowerCase()) || column.key.toLowerCase().includes(fieldSearch.toLowerCase()));
  const filteredMeasures = numericColumns.filter((column) => column.label.toLowerCase().includes(fieldSearch.toLowerCase()) || column.key.toLowerCase().includes(fieldSearch.toLowerCase()));
  const validationMessages = React.useMemo(() => validateChartConfig(data, config), [data, config]);
  const pivotRows = React.useMemo(() => config.chartType === "bubble" ? [] : buildPivotRows(data, config), [data, config]);
  const pivotSeries = React.useMemo(() => config.chartType === "bubble" ? [] : getPivotSeries(data, config), [data, config]);
  const bubblePoints = React.useMemo(() => config.chartType === "bubble" ? buildBubblePoints(data, config) : [], [data, config]);
  const colors = palettes[appearance.palette];
  const hasRenderableData = config.chartType === "bubble" ? bubblePoints.length > 0 : pivotRows.length > 0 && pivotSeries.length > 0;
  const pieValues = config.chartType === "pie"
    ? finiteNumbers(pivotRows.flatMap((row) => pivotSeries.map((item) => row.values[item.key])))
    : [];
  const chartBlockingMessages = [
    ...validationMessages,
    ...(config.chartType === "pie" && pieValues.some((value) => value < 0)
      ? ["Pie charts cannot display negative values. Filter them out or choose a bar chart."]
      : []),
    ...(config.chartType === "pie" && hasRenderableData && !pieValues.some((value) => value > 0)
      ? ["Pie charts require at least one positive value."]
      : []),
  ];

  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const backgroundElements = Array.from(document.body.children)
      .filter((element) => element !== backdropRef.current)
      .map((element) => ({
        element,
        hadInert: element.hasAttribute("inert"),
        ariaHidden: element.getAttribute("aria-hidden"),
      }));
    backgroundElements.forEach(({ element }) => {
      element.setAttribute("inert", "");
      element.setAttribute("aria-hidden", "true");
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.hasAttribute("hidden") && element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      backgroundElements.forEach(({ element, hadInert, ariaHidden }) => {
        if (!hadInert) element.removeAttribute("inert");
        if (ariaHidden == null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      });
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const updateConfig = (patch: Partial<ChartBuilderConfig>) => setConfig((current) => ({ ...current, ...patch }));
  const updateAppearance = (patch: Partial<Appearance>) => setAppearance((current) => ({ ...current, ...patch }));

  const selectChartType = (chartType: ChartType) => {
    setConfig((current) => {
      const next = { ...current, chartType };
      if (chartType === "pie") {
        next.valueFields = current.valueFields.slice(0, 1);
        next.seriesField = undefined;
      }
      if (chartType === "bubble") {
        next.xField = current.xField ?? numericColumns[0]?.key;
        next.yField = current.yField ?? numericColumns[1]?.key ?? numericColumns[0]?.key;
        next.sizeField = current.sizeField ?? numericColumns[2]?.key ?? numericColumns[0]?.key;
        next.labelField = current.labelField ?? dimensionColumns[0]?.key;
        next.colorField = current.colorField ?? dimensionColumns[1]?.key;
      } else {
        next.categoryField = current.categoryField ?? dimensionColumns[0]?.key;
        next.valueFields = next.valueFields.length ? next.valueFields : numericColumns.slice(0, chartType === "pie" ? 1 : 2).map((column) => column.key);
      }
      return next;
    });
  };

  const assignField = (role: FieldRole, key: string) => {
    const column = data.columns.find((item) => item.key === key);
    if (!column) return;
    setConfig((current) => {
      if (role === "filter") {
        if (current.filters.some((filter) => filter.field === key)) return current;
        const filter: ChartFilter = isNumericColumn(column)
          ? { kind: "number", field: key }
          : { kind: "category", field: key, value: data.rows.find((row) => row[key] != null)?.[key] ?? "" };
        return { ...current, filters: [...current.filters, filter] };
      }
      if (role === "values") {
        if (!isNumericColumn(column)) return current;
        const nextValues = current.chartType === "pie" ? [key] : Array.from(new Set([...current.valueFields, key]));
        return { ...current, valueFields: nextValues };
      }
      if (["x", "y", "size"].includes(role) && !isNumericColumn(column)) return current;
      const property: Record<Exclude<FieldRole, "values" | "filter">, keyof ChartBuilderConfig> = {
        category: "categoryField",
        series: "seriesField",
        x: "xField",
        y: "yField",
        size: "sizeField",
        label: "labelField",
        color: "colorField",
      };
      return { ...current, [property[role as Exclude<FieldRole, "values" | "filter">]]: key };
    });
    setDragOverRole(null);
  };

  const removeField = (role: FieldRole, key: string) => {
    setConfig((current) => {
      if (role === "values") return { ...current, valueFields: current.valueFields.filter((field) => field !== key) };
      if (role === "filter") return { ...current, filters: current.filters.filter((filter) => filter.field !== key) };
      const property: Record<Exclude<FieldRole, "values" | "filter">, keyof ChartBuilderConfig> = {
        category: "categoryField",
        series: "seriesField",
        x: "xField",
        y: "yField",
        size: "sizeField",
        label: "labelField",
        color: "colorField",
      };
      return { ...current, [property[role as Exclude<FieldRole, "values" | "filter">]]: undefined };
    });
  };

  const autoAssignField = (key: string) => {
    const column = data.columns.find((item) => item.key === key);
    if (!column) return;
    if (config.chartType === "bubble") {
      if (isNumericColumn(column)) {
        if (!config.xField) assignField("x", key);
        else if (!config.yField) assignField("y", key);
        else assignField("size", key);
      } else if (!config.labelField) assignField("label", key);
      else assignField("color", key);
      return;
    }
    if (isNumericColumn(column)) assignField("values", key);
    else if (!config.categoryField) assignField("category", key);
    else assignField("series", key);
  };

  const chartTypeAvailable = (type: ChartType) => type === "bubble"
    ? numericColumns.length >= 2
    : dimensionColumns.length >= 1 && numericColumns.length >= 1;

  const filterValues = (field: string) => {
    const values: unknown[] = [];
    const seen = new Set<string>();
    data.rows.forEach((row) => {
      const value = row[field];
      const signature = `${typeof value}:${String(value)}`;
      if (!seen.has(signature)) {
        seen.add(signature);
        values.push(value);
      }
    });
    return values.slice(0, 200);
  };

  const updateFilter = (index: number, filter: ChartFilter) => {
    setConfig((current) => ({ ...current, filters: current.filters.map((item, itemIndex) => itemIndex === index ? filter : item) }));
  };

  const addFilter = (key: string) => {
    if (!key || config.filters.some((filter) => filter.field === key)) return;
    assignField("filter", key);
  };

  const exportCurrentData = () => {
    if (config.chartType === "bubble") {
      const headers = ["Label", fieldLabel(data, config.xField), fieldLabel(data, config.yField), fieldLabel(data, config.sizeField), fieldLabel(data, config.colorField)].filter(Boolean);
      const rows = bubblePoints.map((point) => [point.label, point.x, point.y, point.size, point.color ?? ""].slice(0, headers.length));
      downloadText(chartFilename(data.title), [headers, ...rows].map((row) => row.map(safeCsvCell).join(",")).join("\r\n"), "text/csv;charset=utf-8");
      return;
    }
    const headers = [fieldLabel(data, config.categoryField), ...pivotSeries.map((item) => aggregatedSeriesLabel(item, config.aggregation))];
    const rows = pivotRows.map((row) => [row.category, ...pivotSeries.map((item) => row.values[item.key] ?? "")]);
    downloadText(chartFilename(data.title), [headers, ...rows].map((row) => row.map(safeCsvCell).join(",")).join("\r\n"), "text/csv;charset=utf-8");
  };

  const wells = config.chartType === "bubble"
    ? [
      { label: "X axis", role: "x" as const, placeholder: "Drop a numeric field", fields: config.xField ? [config.xField] : [] },
      { label: "Y axis", role: "y" as const, placeholder: "Drop a numeric field", fields: config.yField ? [config.yField] : [] },
      { label: "Bubble size", role: "size" as const, placeholder: "Drop a numeric field", fields: config.sizeField ? [config.sizeField] : [] },
      { label: "Label", role: "label" as const, placeholder: "Optional label", fields: config.labelField ? [config.labelField] : [] },
      { label: "Color", role: "color" as const, placeholder: "Optional group", fields: config.colorField ? [config.colorField] : [] },
    ]
    : [
      { label: config.chartType === "line" ? "X axis" : config.chartType === "pie" ? "Slices" : "Category", role: "category" as const, placeholder: "Drop a dimension", fields: config.categoryField ? [config.categoryField] : [] },
      { label: config.chartType === "line" ? "Y values" : "Values", role: "values" as const, placeholder: "Drop one or more measures", fields: config.valueFields },
      ...(config.chartType !== "pie" ? [{ label: "Series", role: "series" as const, placeholder: "Optional grouping", fields: config.seriesField ? [config.seriesField] : [] }] : []),
    ];

  const dateText = data.date_range?.start || data.date_range?.end
    ? `${data.date_range?.start ?? "…"} to ${data.date_range?.end ?? "…"}`
    : "All returned dates";
  const sourceText = sourceSummary(data);
  const displayedWarning = [
    ...chartBlockingMessages,
    ...(config.chartType !== "bubble" && config.aggregation !== "count" && new Set(config.valueFields.map((field) => fieldUnit(data, field) ?? "number")).size > 1
      ? ["These measures use different units on one axis. For the clearest comparison, keep measures with matching units or chart them separately."]
      : []),
    ...(!data.complete ? (data.completeness_warnings?.length ? data.completeness_warnings : ["This source result may be incomplete."]) : []),
  ];

  const content = <div
    ref={backdropRef}
    className="chart-builder-backdrop"
    onMouseDown={(event) => event.stopPropagation()}
    onPointerDown={(event) => event.stopPropagation()}
    onClick={(event) => event.stopPropagation()}
    onWheel={(event) => event.stopPropagation()}
  >
    <div ref={dialogRef} className="chart-builder-shell" role="dialog" aria-modal="true" aria-label={`Chart Builder: ${data.title}`}>
      <header className="chart-builder-header">
        <div className="chart-builder-heading">
          <div className="chart-builder-mark" aria-hidden="true">⌁</div>
          <div className="chart-builder-title-wrap">
            <div className="chart-builder-eyebrow">Lenses Chart Builder</div>
            <div className="chart-builder-title">{data.title}</div>
          </div>
        </div>
        <div className="chart-builder-header-actions">
          <button type="button" className="chart-builder-button" onClick={() => { setConfig(defaultConfig); setAppearance({ showLegend: true, showLabels: false, showGrid: true, stacked: false, donut: true, showPoints: true, palette: "emerald" }); }}>Reset</button>
          <button type="button" className="chart-builder-button" onClick={exportCurrentData} disabled={!hasRenderableData}>Download CSV</button>
          <button type="button" className="chart-builder-button is-primary" onClick={onClose}>Done</button>
          <button type="button" className="chart-builder-icon-button" aria-label="Close chart builder" onClick={onClose} autoFocus>×</button>
        </div>
      </header>

      <main className="chart-builder-main">
        <aside className="chart-builder-sidebar" aria-label="Available fields">
          <div className="chart-builder-sidebar-header">
            <span className="chart-builder-section-label">Fields</span>
            <span className="chart-builder-count">{data.columns.length}</span>
          </div>
          <input className="chart-builder-search" type="search" placeholder="Find a field" value={fieldSearch} onChange={(event) => setFieldSearch(event.target.value)} />
          <div className="chart-builder-field-group">
            <span className="chart-builder-section-label">Dimensions</span>
            <div className="chart-builder-field-list">
              {filteredDimensions.map((column) => <button
                key={column.key}
                type="button"
                className="chart-builder-field"
                draggable
                title="Drag to a field well or click to add"
                onDragStart={(event) => {
                  event.dataTransfer.setData("application/x-lenses-field", column.key);
                  event.dataTransfer.setData("text/plain", column.key);
                  event.dataTransfer.effectAllowed = "move";
                }}
                onClick={() => autoAssignField(column.key)}
              >
                <span className="chart-builder-field-icon">Aa</span>
                <span className="chart-builder-field-name">{column.label}</span>
                <span className="chart-builder-field-type">{column.type === "date" ? "date" : "text"}</span>
              </button>)}
            </div>
          </div>
          <div className="chart-builder-field-group">
            <span className="chart-builder-section-label">Measures</span>
            <div className="chart-builder-field-list">
              {filteredMeasures.map((column) => <button
                key={column.key}
                type="button"
                className="chart-builder-field is-measure"
                draggable
                title="Drag to a field well or click to add"
                onDragStart={(event) => {
                  event.dataTransfer.setData("application/x-lenses-field", column.key);
                  event.dataTransfer.setData("text/plain", column.key);
                  event.dataTransfer.effectAllowed = "move";
                }}
                onClick={() => autoAssignField(column.key)}
              >
                <span className="chart-builder-field-icon">#</span>
                <span className="chart-builder-field-name">{column.label}</span>
                <span className="chart-builder-field-type">{column.unit ?? "number"}</span>
              </button>)}
            </div>
          </div>
          {!filteredDimensions.length && !filteredMeasures.length ? <div className="chart-builder-filter-empty">No fields match “{fieldSearch}”.</div> : null}
        </aside>

        <section className="chart-builder-workspace" aria-label="Chart workspace">
          <div className="chart-builder-toolbar">
            <div className="chart-builder-types" role="group" aria-label="Chart type">
              {(["bar", "line", "pie", "bubble"] as ChartType[]).map((type) => <button
                key={type}
                type="button"
                className={`chart-builder-chart-type${config.chartType === type ? " is-active" : ""}`}
                aria-pressed={config.chartType === type}
                disabled={!chartTypeAvailable(type)}
                title={!chartTypeAvailable(type) ? (type === "bubble" ? "Bubble charts need at least two numeric fields" : "This chart needs a dimension and a numeric field") : undefined}
                onClick={() => selectChartType(type)}
              ><span aria-hidden="true">{chartTypeIcons[type]}</span>{chartTypeLabels[type]}</button>)}
            </div>
            <div className="chart-builder-inline-actions">
              <span className="chart-builder-count">{config.chartType === "bubble" ? bubblePoints.length : pivotRows.length} plotted</span>
              <button type="button" className="chart-builder-button" aria-pressed={showData} onClick={() => setShowData((value) => !value)}>{showData ? "Hide data" : "Show data"}</button>
            </div>
          </div>

          <div className="chart-builder-wells">
            {wells.map((well) => <ChartWell
              key={well.role}
              {...well}
              data={data}
              dragOverRole={dragOverRole}
              onDropField={assignField}
              onRemoveField={removeField}
            />)}
            <ChartWell
              label="Filters"
              role="filter"
              placeholder="Drop any field"
              fields={config.filters.map((filter) => filter.field)}
              data={data}
              dragOverRole={dragOverRole}
              onDropField={assignField}
              onRemoveField={removeField}
            />
          </div>

          <div className="chart-builder-preview-card">
            <div className="chart-builder-preview-header">
              <div>
                <div className="chart-builder-preview-title">{data.title}</div>
                <div className="chart-builder-preview-subtitle">{dateText} · {config.aggregation} aggregation · {data.rows.length.toLocaleString()} authorized rows</div>
              </div>
              <span className="chart-builder-count">Live preview</span>
            </div>
            {displayedWarning.length ? <div className="chart-builder-warning" role="alert">{displayedWarning.join(" ")}</div> : null}
            <div className="chart-builder-chart-area">
              {chartBlockingMessages.length || !hasRenderableData ? <div className="chart-builder-empty">
                <div>
                  <div className="chart-builder-empty-icon" aria-hidden="true">{chartTypeIcons[config.chartType]}</div>
                  <strong>{chartBlockingMessages.length ? "This chart needs attention" : "No plottable rows"}</strong>
                  <span>{chartBlockingMessages[0] ?? "Adjust the filters or choose fields containing values."}</span>
                </div>
              </div> : config.chartType === "bar" ? <BarChart data={data} rows={pivotRows} series={pivotSeries} aggregation={config.aggregation} appearance={appearance} colors={colors} onTooltip={setTooltip} />
                : config.chartType === "line" ? <LineChart data={data} rows={pivotRows} series={pivotSeries} aggregation={config.aggregation} appearance={appearance} colors={colors} onTooltip={setTooltip} />
                  : config.chartType === "pie" ? <PieChart data={data} rows={pivotRows} series={pivotSeries} aggregation={config.aggregation} appearance={appearance} colors={colors} onTooltip={setTooltip} />
                    : <BubbleChart data={data} points={bubblePoints} config={config} appearance={appearance} colors={colors} onTooltip={setTooltip} />}
            </div>
            {appearance.showLegend && hasRenderableData && !chartBlockingMessages.length ? <div className="chart-builder-legend">
              {(config.chartType === "pie"
                ? pivotRows.map((row, index) => ({ label: row.category, color: colors[index % colors.length] }))
                : config.chartType === "bubble"
                ? Array.from(new Set(bubblePoints.map((point) => point.color ?? "All points"))).map((label, index) => ({ label, color: colors[index % colors.length] }))
                : pivotSeries.map((item, index) => ({ label: aggregatedSeriesLabel(item, config.aggregation), color: colors[index % colors.length] })))
                .map((item) => <span className="chart-builder-legend-item" key={item.label}><i className="chart-builder-legend-swatch" style={{ background: item.color }} />{item.label}</span>)}
            </div> : null}
          </div>

          {showData ? <div className="chart-builder-data-card">
            <div className="chart-builder-data-header">
              <span className="chart-builder-section-label">Pivot data</span>
              <span className="chart-builder-count">{config.chartType === "bubble" ? bubblePoints.length : pivotRows.length} rows</span>
            </div>
            <div className="chart-builder-data-scroll">
              <table className="chart-builder-table">
                <thead><tr>{config.chartType === "bubble" ? <>
                  <th>Label</th><th>{fieldLabel(data, config.xField)}</th><th>{fieldLabel(data, config.yField)}</th><th>{fieldLabel(data, config.sizeField)}</th>{config.colorField ? <th>{fieldLabel(data, config.colorField)}</th> : null}
                </> : <><th>{fieldLabel(data, config.categoryField)}</th>{pivotSeries.map((item) => <th key={item.key}>{aggregatedSeriesLabel(item, config.aggregation)}</th>)}</>}</tr></thead>
                <tbody>{config.chartType === "bubble"
                  ? bubblePoints.map((point, index) => <tr key={`${point.rowIndex}-${index}`}><td>{point.label}</td><td>{formatNumber(point.x, fieldUnit(data, config.xField))}</td><td>{formatNumber(point.y, fieldUnit(data, config.yField))}</td><td>{formatNumber(point.size, fieldUnit(data, config.sizeField))}</td>{config.colorField ? <td>{point.color ?? "—"}</td> : null}</tr>)
                  : pivotRows.map((row, index) => <tr key={`${row.category}-${index}`}><td>{row.category}</td>{pivotSeries.map((item) => <td key={item.key}>{row.values[item.key] == null ? "—" : formatNumber(row.values[item.key] as number, aggregatedUnit(data, item.valueField, config.aggregation))}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div> : null}
        </section>

        <aside className="chart-builder-inspector" aria-label="Chart options">
          <div className="chart-builder-inspector-header">
            <span className="chart-builder-section-label">Options</span>
            <span className="chart-builder-count">{chartTypeLabels[config.chartType]}</span>
          </div>

          {config.chartType !== "bubble" ? <div className="chart-builder-option-section">
            <span className="chart-builder-section-label">Data</span>
            <label className="chart-builder-control-label">Aggregation
              <select className="chart-builder-control" value={config.aggregation} onChange={(event) => updateConfig({ aggregation: event.target.value as Aggregation })}>
                {(Object.keys(aggregationLabels) as Aggregation[]).map((aggregation) => <option key={aggregation} value={aggregation}>{aggregationLabels[aggregation]}</option>)}
              </select>
            </label>
            <div className="chart-builder-option-grid">
              <label className="chart-builder-control-label">Sort by
                <select className="chart-builder-control" value={config.sort.by} onChange={(event) => updateConfig({ sort: { ...config.sort, by: event.target.value as "category" | "value" } })}>
                  <option value="category">Category</option><option value="value">Value</option>
                </select>
              </label>
              <label className="chart-builder-control-label">Direction
                <select className="chart-builder-control" value={config.sort.direction} onChange={(event) => updateConfig({ sort: { ...config.sort, direction: event.target.value as "asc" | "desc" } })}>
                  <option value="asc">Ascending</option><option value="desc">Descending</option>
                </select>
              </label>
            </div>
            <label className="chart-builder-control-label">Maximum categories
              <input className="chart-builder-control" type="number" min={1} max={100} value={config.topN ?? ""} onChange={(event) => updateConfig({ topN: event.target.value ? Math.max(1, Math.min(100, Number(event.target.value))) : undefined })} />
            </label>
          </div> : <div className="chart-builder-option-section">
            <span className="chart-builder-section-label">Data</span>
            <div className="chart-builder-option-grid">
              <label className="chart-builder-control-label">Sort by
                <select className="chart-builder-control" value={config.sort.by} onChange={(event) => updateConfig({ sort: { ...config.sort, by: event.target.value as "category" | "value" } })}>
                  <option value="category">Label</option><option value="value">Bubble size</option>
                </select>
              </label>
              <label className="chart-builder-control-label">Direction
                <select className="chart-builder-control" value={config.sort.direction} onChange={(event) => updateConfig({ sort: { ...config.sort, direction: event.target.value as "asc" | "desc" } })}>
                  <option value="asc">Ascending</option><option value="desc">Descending</option>
                </select>
              </label>
            </div>
            <label className="chart-builder-control-label">Maximum bubbles
              <input className="chart-builder-control" type="number" min={1} max={100} value={config.topN ?? ""} onChange={(event) => updateConfig({ topN: event.target.value ? Math.max(1, Math.min(100, Number(event.target.value))) : undefined })} />
            </label>
          </div>}

          <div className="chart-builder-option-section">
            <span className="chart-builder-section-label">Appearance</span>
            <label className="chart-builder-control-label">Color palette
              <select className="chart-builder-control" value={appearance.palette} onChange={(event) => updateAppearance({ palette: event.target.value as Appearance["palette"] })}>
                <option value="emerald">Emerald</option><option value="ocean">Ocean</option><option value="orchard">Orchard</option>
              </select>
            </label>
            <Toggle label="Show legend" checked={appearance.showLegend} onChange={(checked) => updateAppearance({ showLegend: checked })} />
            <Toggle label="Show data labels" checked={appearance.showLabels} onChange={(checked) => updateAppearance({ showLabels: checked })} />
            {config.chartType !== "pie" ? <Toggle label="Show gridlines" checked={appearance.showGrid} onChange={(checked) => updateAppearance({ showGrid: checked })} /> : null}
            {config.chartType === "bar" && pivotSeries.length > 1 ? <Toggle label="Stack series" checked={appearance.stacked} onChange={(checked) => updateAppearance({ stacked: checked })} /> : null}
            {config.chartType === "line" ? <Toggle label="Show points" checked={appearance.showPoints} onChange={(checked) => updateAppearance({ showPoints: checked })} /> : null}
            {config.chartType === "pie" ? <Toggle label="Donut style" checked={appearance.donut} onChange={(checked) => updateAppearance({ donut: checked })} /> : null}
          </div>

          <div className="chart-builder-option-section">
            <span className="chart-builder-section-label">Filters</span>
            <label className="chart-builder-control-label">Add a field
              <select className="chart-builder-control" value="" onChange={(event) => addFilter(event.target.value)}>
                <option value="">Choose field…</option>
                {data.columns.filter((column) => !config.filters.some((filter) => filter.field === column.key)).map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}
              </select>
            </label>
            {config.filters.length ? config.filters.map((filter, index) => <div className="chart-builder-filter-card" key={`${filter.field}-${index}`}>
              <div className="chart-builder-filter-heading"><span>{fieldLabel(data, filter.field)}</span><button type="button" aria-label={`Remove ${fieldLabel(data, filter.field)} filter`} onClick={() => removeField("filter", filter.field)}>Remove</button></div>
              {filter.kind === "category" ? <select
                className="chart-builder-control"
                aria-label={`${fieldLabel(data, filter.field)} value`}
                value={String(filterValues(filter.field).findIndex((value) => String(value) === String(filter.value)))}
                onChange={(event) => updateFilter(index, { ...filter, value: filterValues(filter.field)[Number(event.target.value)] })}
              >{filterValues(filter.field).map((value, valueIndex) => <option key={`${String(value)}-${valueIndex}`} value={valueIndex}>{value == null || value === "" ? "Unspecified" : String(value)}</option>)}</select> : <div className="chart-builder-option-grid">
                <label className="chart-builder-control-label">Minimum<input className="chart-builder-control" type="number" value={filter.min ?? ""} onChange={(event) => updateFilter(index, { ...filter, min: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
                <label className="chart-builder-control-label">Maximum<input className="chart-builder-control" type="number" value={filter.max ?? ""} onChange={(event) => updateFilter(index, { ...filter, max: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
              </div>}
            </div>) : <div className="chart-builder-filter-empty">Drag any field into the Filters well or choose one above.</div>}
          </div>

          <div className="chart-builder-source">
            <strong>Source & coverage</strong>
            <div>{sourceText || "Ask Lenses authorized result"}</div>
            <div>{dateText}</div>
            <div>{data.complete ? "Complete result" : "Review the completeness warning before sharing."}</div>
          </div>
        </aside>
      </main>
    </div>
    {tooltip ? <div className="chart-builder-tooltip" style={{ left: tooltip.x, top: tooltip.y }} role="tooltip">
      <strong>{tooltip.title}</strong>{tooltip.lines.map((line) => <div key={line}>{line}</div>)}
    </div> : null}
  </div>;

  return createPortal(content, document.body);
}
